import React, {useCallback, useEffect, useMemo} from 'react';
import {Grid} from "@material-ui/core";
import {Form, Formik} from "formik";
import {useDispatch, useSelector} from "react-redux";
import {StyledButton} from "../../../ui-base/button";
import * as Yup from 'yup';
import {useHistory} from "react-router-dom";
import styled from "styled-components";
import {Colors} from "../../../../theme/colors";
import {RequireInfo} from "../../../ui-base/require-info";
import {useTranslation} from "react-i18next";
import {AddBookFormStep1} from './add-book-form-step1';
import {AddBookFormStep2} from './add-book-form-step2';
import {AddBookFormStep3} from "./add-book-form-step3";
import {AddBookFormModel} from "../../../../models/forms/publisher/add-book-form.model";
import {setProgressValue, toggleLoader} from "../../../../store/global/global-slice";
import {PublisherService} from "../../../../services/publisher.service";
import {PublisherBookModel} from "../../../../models/publisher/publisher-book.model";
import {AllLanguages} from "../../../../data/languages";
import {CountryModel} from "../../../../models/country.model";
import {RootState} from "../../../../store/root-reducer";
import {CategoryModel} from "../../../../models/category.model";
import {CategoryCsvModel} from "../../../../models/category-csv.model";
import {BaseService} from "../../../../services/base.service";

const AddBookSchema = Yup.object().shape({
	title: Yup.string().required(),
	titleEn: Yup.string().required(),
	author: Yup.string().required(),
	originalLang: Yup.mixed().required(),
	mainCategory: Yup.string().required(),
	categories: Yup.array().min(1).required(),
	file: Yup.mixed().test('fileType', 'wrongFileFormat', value => value ? ['application/pdf', 'text/plain'].includes(value.type) : true),
	cover: Yup.mixed().test('fileType', 'Unsupported File Format', value => value ? ['image/jpeg', 'image/png'].includes(value.type) : true),
});

export const AddBookForm: React.FC<{book?: PublisherBookModel}> = ({book}) => {
	const [bookId, setBookId] = React.useState<string>('');
	const [activeStep, setActiveStep] = React.useState<number>(1);
	const [contentFile, setContentFile] = React.useState(null);
	const [contentCover, setContentCover] = React.useState(null);
	const dispatch = useDispatch();
	const history = useHistory();
	const {t} = useTranslation(['publisher', 'common']);
	const languages: {language: string, code: string}[] = AllLanguages;

	const countries: CountryModel[] = useSelector(
		(state: RootState) => state.countries.countries
	);

	const themaCategories: CategoryModel[] = useSelector(
		(state: RootState) => state.categories.categories
	);

	const categoriesCsv: CategoryCsvModel[] = useSelector(
		(state: RootState) => state.categories.categoriesCsv
	);

	const formInitialValues = useMemo(() => ({
		bookStatus: 'CREATE',
		isbn: '',
		author: '',
		mainCategory: '',
		categories: [],
		title: '',
		titleEn: '',
		originalLang: null,
		translatedTo: [],
		countryWithLicense: [],
		file: null,
		cover: null,
		tags: [],
		description: '',
		summary: '',
		review: '',
		partOfBook: '',
		tableOfContents: '',
		searchPublisher: false,
		searchPublisherCountries: [],
		servicesContractors: false,
		translation: false,
		editors: false,
		corrector: false,
		graphic: false,
		reviewer: false,
		publishMarket: false,
		numberOfBooks: '',
		releaseDate: null,
		numberOfReleases: '',
		numberOfSold: '',
		prizes: [],
		langToLang: [{fromLang: null, toLang: null}]
	}), []);

	const formSubmit = async (values: AddBookFormModel) => {
		if (activeStep === 3) {
			dispatch(toggleLoader(true));

			const bookRes = bookId ?
				await PublisherService.editBook(values, bookId) :
				await PublisherService.addBook(values);

			if (bookRes.status && (values.file || values.cover)) {

				const filesRes = await PublisherService.uploadFilesToBook(
					values.file || '',
					values.cover || '',
					bookRes.data.id,
					(progress) => {
						dispatch(setProgressValue(progress))
					}
				);

				if (filesRes) {
					history.push(`/publisher/my-books/details/${bookRes.data.id}`);
				}

				dispatch(toggleLoader(false));
			} else if (bookRes.status) {
				history.push(`/publisher/my-books/details/${bookRes.data.id}`);
				dispatch(toggleLoader(false));
			}
		} else {
			goToNextStep();
		}
	}

	const setIniValues = useCallback((book: PublisherBookModel) => {
		setBookId(book.id);
		formInitialValues.bookStatus = book.phase;
		formInitialValues.isbn = book.isbn;
		formInitialValues.author = book.authors;
		formInitialValues.mainCategory = BaseService.parseOldMainCategory(book.main_category);
		formInitialValues.title = book.original_title;
		formInitialValues.titleEn = book.en_title;
		formInitialValues.tags = book.tags;
		formInitialValues.description = book.description;
		formInitialValues.summary = book.summary;
		formInitialValues.review = book.review;
		formInitialValues.partOfBook = book.fragment;
		formInitialValues.tableOfContents = book.table_of_contents;
		formInitialValues.editors = book.looking_for_editor;
		formInitialValues.corrector = book.looking_for_corrector;
		formInitialValues.graphic = book.looking_for_illustration;
		formInitialValues.reviewer = book.looking_for_reviewing;
		formInitialValues.publishMarket = book.published_at_the_market;
		formInitialValues.numberOfBooks = book.saledata?.copies?.toString() || '';
		formInitialValues.releaseDate = book.saledata?.publish_date?.toString() || null;
		formInitialValues.numberOfReleases = book.saledata?.editions?.toString() || '';
		formInitialValues.numberOfSold = book.saledata?.sold_copies?.toString() || '';
		formInitialValues.prizes = book.awards || [];

		formInitialValues.originalLang = book.book_language ?
			languages.find(lang => lang.code === book.book_language) : null;

		formInitialValues.translation = book.translation_wishes?.length > 0;
		formInitialValues.langToLang = book.translation_wishes.map((translation) => (
			{
				fromLang: languages.find((lang) => lang.code === translation.language_from.code),
				toLang: languages.find((lang) => lang.code === translation.language_to.code)
			}
		)) || [{fromLang: null, toLang: null}];

		formInitialValues.translatedTo = book.translations?.map((language) => (
			languages.find(lang => lang.code === language.language)
		)) || [];

		formInitialValues.countryWithLicense = book.countries_sold_to.map((iso) => {
			return countries.find((country) => country.iso === iso);
		});

		const filteredCategories: string[] = categoriesCsv.filter((category) => category[BaseService.parseOldMainCategory(book.main_category)] === 'TRUE' && category['Do wyrzucenia'] === 'FALSE').map((category) => category.Code);
		formInitialValues.categories = book.categories ?
			book.categories.map((category) => {
				const foundCategory = themaCategories.find((cat) => cat.code === category.code);

				const children = themaCategories.filter((category) => filteredCategories.includes(category.code) && category.parent_code === foundCategory.code);

				if (children && children.length > 0) {
					return {
						...foundCategory,
						id: foundCategory.id,
						name: t('categoriesComponent.general'),
						description: t('categoriesComponent.general'),
						children: [],
						code: foundCategory.code + '_GENERALCATEGORY',
						parent_code: foundCategory.code
					}
				} else {
					return foundCategory;
				}
			})
			: [];

		formInitialValues.servicesContractors = formInitialValues.translation || formInitialValues.editors || formInitialValues.corrector || formInitialValues.graphic || formInitialValues.reviewer;

		//not add to create / publiser model
		formInitialValues.searchPublisherCountries = book.foreign_publisher_countries.map((iso) => {
			return countries.find((country) => country.iso === iso);
		});
		formInitialValues.searchPublisher = false;

		setContentFile(BaseService.getFileName(book.content_full?.file_purpose, book.content_full?.file));
		setContentCover(BaseService.getFileName(book.cover?.file_purpose, book.cover?.file));
	}, [categoriesCsv, countries, formInitialValues, languages, t, themaCategories]);

	useEffect(() => {
		if (book) {
			setIniValues(book);
		}
	}, [book, setIniValues])

	const goToNextStep = () => {
		setActiveStep(activeStep + 1);
	}

	const goToPreviousStep = () => {
		setActiveStep(activeStep - 1);
	}

	return (
		<Formik
			initialValues={formInitialValues}
			onSubmit={(values: any) => {
				formSubmit(values);
			}}
			validationSchema={AddBookSchema}
		>
			{({values, touched, errors, handleChange, handleBlur, isValid, setFieldValue}) => (
				<Form>
					<Grid container direction="row" justifyContent="space-between" alignItems={"center"} spacing={2}>
						<Grid item>
							<StyledTitle>
								{activeStep === 1 && t('addBookNew.title1')}
								{activeStep === 2 && t('addBookNew.title2')}
								{activeStep === 3 && t('addBookNew.title3')}
							</StyledTitle>
						</Grid>

						<Grid item>
							<Grid item container direction={"row"} spacing={1}>
								{activeStep > 1 &&
                                    <Grid item>
                                        <StyledButton type="button" color={'secondary'} onClick={goToPreviousStep}>
											{'< ' + t('addBookNew.back')}
                                        </StyledButton>
                                    </Grid>
								}

								{activeStep === 1 &&
                                    <Grid item>
                                        <StyledButton type="submit" color={'violet'}>
											{t('addBookNew.next') + ' >'}
                                        </StyledButton>
                                    </Grid>
								}

								{activeStep === 2 &&
                                    <Grid item>
                                        <StyledButton type="button" onClick={goToNextStep} color={'violet'}>
											{t('addBookNew.next') + ' >'}
                                        </StyledButton>
                                    </Grid>
								}

								{activeStep === 3 &&
                                    <Grid item>
                                        <StyledButton type="submit" color={'violet'}>
											{t('addBookNew.save')}
                                        </StyledButton>
                                    </Grid>
								}
							</Grid>
						</Grid>
					</Grid>

					<StyledNotice>
						{t('addBookNew.notice')}
					</StyledNotice>

					{activeStep === 1 && <AddBookFormStep1
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        values={values}
                        errors={errors}
                        touched={touched}
                        contentFile={contentFile}
						setContentFile={setContentFile}
                        contentCover={contentCover}
						setContentCover={setContentCover}
                    />}

					{activeStep === 2 && <AddBookFormStep2
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        values={values}
                        errors={errors}
                        touched={touched}
                    />}

					{activeStep === 3 && <AddBookFormStep3
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        values={values}
                        errors={errors}
                        touched={touched}
                    />}
					<RequireInfo></RequireInfo>
				</Form>
			)}
		</Formik>
	)
}

const StyledTitle = styled('h5')`
  font-weight: 600;
  font-size: 1.4rem;
  color: ${Colors.lightViolet};
`;

const StyledNotice = styled('p')`
  font-size: 1rem;
  color: ${Colors.gray2};
  margin-top: 0.5rem;
`;
